<template>
    <div class="rings-block" :style="{ width: `${size}px` }">
        <div class="rings-wrap">
            <!-- Hide the rings from assistive technology because the adjacent cards provide the same values
                 as text.
                 -->
            <svg :viewBox="`0 0 ${size} ${size}`" aria-hidden="true" class="rings">
                <g v-for="(ring, position) in rings" :key="ring.id">
                    <!-- Use neutral tracks; tracks tinted like their fills were mistaken for a second data
                         series.
                         -->
                    <circle
                        :cx="centre"
                        :cy="centre"
                        :r="ring.radius"
                        fill="none"
                        :stroke-width="stroke"
                        class="track"
                        :class="{ 'is-waiting': ring.value === 0 && ring.step !== null }" />

                    <circle
                        v-if="ring.value > 0"
                        :cx="centre"
                        :cy="centre"
                        :r="ring.radius"
                        fill="none"
                        :stroke="ring.palette.dot"
                        :stroke-width="stroke"
                        stroke-linecap="round"
                        :stroke-dasharray="swept ? ring.fillDash : `0.01 ${ring.circumference}`"
                        :stroke-dashoffset="ring.fillOffset"
                        :transform="`rotate(-90 ${centre} ${centre})`"
                        class="fill"
                        :style="{ transitionDelay: `${position * 280}ms` }" />
                </g>
            </svg>

            <div class="centre-stack">
                <div class="centre-value">
                    <span class="figure" :class="{ 'is-compact': compact }">{{ formatNumber(percent / 100, "percent") }}</span>
                    <span class="caption">{{ t("onboarding.profileSetup.rings.caption") }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * Server values share artifacts with SetupStep.done. Show current values only; projected values belong in
 * the cards because a second arc looked like another quantity.
 */
import type { ContextDimension } from "~vue/ProfileSetup/types";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { ringPalette } from "./contextRingColors";

const { t } = useI18n();

const {
    dimensions,
    percent,
    size = 260,
} = defineProps<{
    dimensions: ContextDimension[];
    percent: number;
    size?: number;
}>();

const RADIUS_RATIOS = [0.46, 0.385, 0.31];
const STROKE_RATIO = 0.05;
const SWEEP_START_DELAY_MS = 120;
const COMPACT_FIGURE_BELOW = 220;

const compact = computed(() => size < COMPACT_FIGURE_BELOW);

const centre = computed(() => size / 2);
const stroke = computed(() => size * STROKE_RATIO);

const rings = computed(() =>
    dimensions.map((dimension, index) => {
        const radius = size * (RADIUS_RATIOS[index] ?? RADIUS_RATIOS.at(-1)!);
        const circumference = 2 * Math.PI * radius;
        /*
         * Round caps extend half a stroke beyond each endpoint. Inset both ends so the painted arc
         * represents the actual value.
         */
        const cap = stroke.value / 2;
        const arc = (share: number) => ({
            dasharray: `${Math.max(share * circumference - stroke.value, 0.01)} ${circumference}`,
            dashoffset: -cap,
        });
        const fill = dimension.value >= 1 ? { dasharray: `${circumference} 0`, dashoffset: 0 } : arc(dimension.value);
        return {
            ...dimension,
            radius,
            circumference,
            palette: ringPalette(dimension.id),
            fillDash: fill.dasharray,
            fillOffset: fill.dashoffset,
        };
    }),
);

const swept = ref(false);
let sweepTimer: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
    sweepTimer = setTimeout(() => {
        swept.value = true;
    }, SWEEP_START_DELAY_MS);
});

onBeforeUnmount(() => {
    if (sweepTimer !== null) {
        clearTimeout(sweepTimer);
    }
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .rings-block {
        @apply max-w-full shrink-0;
    }

    .rings-wrap {
        @apply relative aspect-square w-full;
    }

    .rings {
        @apply relative block h-full w-full;
    }

    .track {
        stroke: var(--color-border);
        transition: stroke 0.3s ease;
    }

    .track.is-waiting {
        animation: ring-breathe 2.6s ease-in-out infinite;
    }

    .fill {
        transition: stroke-dasharray 1.2s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .centre-stack {
        @apply pointer-events-none absolute inset-0 flex items-center justify-center;
    }

    /*
     * Keep padding between the center figure and inner ring; a full ring otherwise looks like an underline.
     */
    .centre-value {
        @apply flex flex-col items-center px-6 text-center;
    }

    /*
     * The design system requires Space Grotesk for numbers, but scaffold utilities stop at 16px, so display
     * sizes need explicit styling.
     */
    .figure {
        @apply font-scaffold font-medium text-[30px] leading-[1.16] text-ink tabular-nums;
    }

    .figure.is-compact {
        @apply text-[24px];
    }

    .caption {
        @apply details mt-1 max-w-full truncate text-ink-muted;
    }
}

@keyframes ring-breathe {
    0%,
    100% {
        stroke: var(--color-border);
    }
    50% {
        stroke: var(--color-border-strong);
    }
}

@media (prefers-reduced-motion: reduce) {
    .fill {
        transition: none;
    }

    .track.is-waiting {
        animation: none;
    }
}
</style>
